/**
 * Credit allocations at provider accounts, their alerts and the accounts'
 * movements (`docs/31_SMS_PROVIDERS.md` §9) — staff's alone.
 *
 * A client's credit purchase that waits on a load has an allocation: the load
 * of its account at the vendor. Where the vendor has a load API, Connect does
 * it and this only shows where it stands; where it has none (Broadcast), the
 * steps are here and staff record the load on the purchase — the same call
 * the Credit purchases page makes, so there is one path to the credits.
 * Every rule is the server's: whether the account can carry it, when it
 * resumes, whether money may be returned while a load is in flight.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  EmptyState,
  Input,
  QueryPending,
  ScrollArea,
  StatusChip,
  api,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';
import { refusalText } from './api-refusal';

export interface Allocation {
  public_id: string;
  /**
   * `purchase` — a credit purchase waits on it; `reconcile` — credits the client
   * already holds that its vendor balance did not pay for (`docs/31` §10).
   */
  kind: 'purchase' | 'reconcile';
  /** The purchase it loads for; none for a reconciliation load. */
  purchase: { public_id: string; status: string } | null;
  client: { public_id: string; name: string };
  account: { public_id: string; label: string } | null;
  connection: string;
  customer_ref: string | null;
  credits: number;
  amount: string;
  account_debit: string | null;
  method: 'api' | 'panel';
  status: AllocationStatus;
  reference: string;
  vendor_reference: string | null;
  attempts: number;
  last_error: string | null;
  submitted_at: string | null;
  confirmed_at: string | null;
  sync_status: 'not_applicable' | 'pending' | 'synced' | 'review';
  synced_at: string | null;
  at: string;
}

export type AllocationStatus =
  | 'waiting_link'
  | 'waiting_funds'
  | 'ready'
  | 'submitted'
  | 'failed'
  | 'review'
  | 'confirmed'
  | 'cancelled';

export interface ProviderAlertRow {
  public_id: string;
  kind: string;
  subject: string | null;
  message: string;
  details: Record<string, string | number | null>;
  at: string;
  notified_at: string;
}

export interface AccountEntry {
  public_id: string;
  kind: 'reload' | 'refund' | 'adjustment' | 'allocation';
  amount: string;
  reference: string | null;
  note: string | null;
  at: string;
}

const STATUS_LABELS: Record<AllocationStatus, string> = {
  waiting_link: 'Not linked',
  waiting_funds: 'Account short',
  ready: 'To load',
  submitted: 'Loaded, not confirmed',
  failed: 'Refused',
  review: 'Needs a person',
  confirmed: 'Added',
  cancelled: 'Money returned',
};
const STATUS_TONES = {
  waiting_link: 'warning',
  waiting_funds: 'danger',
  ready: 'info',
  submitted: 'info',
  failed: 'danger',
  review: 'warning',
  confirmed: 'success',
  cancelled: 'neutral',
} as const;
const SYNC_LABELS = {
  not_applicable: '—',
  pending: 'Not in a reading yet',
  synced: 'In the balance',
  review: 'Not seen — record the balance',
};
const ENTRY_LABELS = { reload: 'Reload', refund: 'Refund', adjustment: 'Adjustment', allocation: 'Client loaded' };

export const allocationKeys = {
  queue: (state: string) => ['provider-allocations', state] as const,
};

/** Every allocation waiting on a provider, across accounts. */
export function AllocationQueue({ panelUrls }: { panelUrls: Record<string, string | null> }) {
  const queue = useQuery({
    queryKey: allocationKeys.queue('open'),
    queryFn: () => api.get<{ allocations: Allocation[] }>('/messaging/provider-accounts/allocations', { params: { state: 'open' } }),
  });
  if (queue.data === undefined) return <QueryPending query={queue} label="Loading allocations" />;
  return (
    <AllocationsCard
      title="Purchases waiting on a provider"
      description="Clients' SMS credit purchases whose accounts at the vendor are still to be loaded. The client sees Processing until they are."
      rows={queue.data?.allocations ?? []}
      panelUrls={panelUrls}
      empty="Nothing waits on a provider."
    />
  );
}

export function AllocationsCard({
  title,
  description,
  rows,
  panelUrls,
  empty,
}: {
  title: string;
  description: string;
  rows: Allocation[];
  panelUrls: Record<string, string | null>;
  empty: string;
}) {
  const [open, setOpen] = useState<Allocation | null>(null);
  return (
    <Card className="overflow-hidden">
      <CardHeader title={title} description={description} />
      {rows.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">{empty}</p>
        </CardBody>
      ) : (
        <ScrollArea className="">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-2xs uppercase tracking-wide text-faint">
              <tr>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 text-right font-medium">Credits</th>
                <th className="px-3 py-2 text-right font-medium">Load</th>
                <th className="px-3 py-2 font-medium">How</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.public_id}
                  className="cursor-pointer border-t border-line hover:bg-surface-2"
                  onClick={() => setOpen(row)}
                >
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{formatDateTime(row.at)}</td>
                  <td className="px-3 py-2">
                    {row.client.name}
                    {row.kind === 'reconcile' ? (
                      <Badge tone="info" className="ml-2">
                        Reconciliation
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatNumber(row.credits)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatMetered(row.amount)}</td>
                  <td className="px-3 py-2 text-xs text-muted">{row.method === 'api' ? 'By API' : 'In the panel'}</td>
                  <td className="px-3 py-2">
                    <StatusChip value={row.status} labels={STATUS_LABELS} tones={STATUS_TONES} />
                  </td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {row.status === 'confirmed' ? SYNC_LABELS[row.sync_status] : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      )}
      {open ? (
        <AllocationDrawer
          allocation={open}
          panelUrl={open.account ? (panelUrls[open.account.public_id] ?? null) : null}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  );
}

function AllocationDrawer({
  allocation,
  panelUrl,
  onClose,
}: {
  allocation: Allocation;
  panelUrl: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const canApprove = useCan('messaging.credit_purchase.approve');
  const canRetry = useCan('messaging.provider_account.create');
  const reconcile = allocation.purchase === null;
  // A reconciliation load has no purchase: staff record it, or call it off,
  // on the allocation itself; a purchase's load is recorded on the purchase.
  const canRecord = reconcile ? canRetry : canApprove;
  const [reference, setReference] = useState('');
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['provider-allocations'] });
    void queryClient.invalidateQueries({ queryKey: ['provider-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
  };
  // The purchase's own steps — the one path to the credits (doc 28 §5).
  const onPurchase = useMutation({
    mutationFn: ({ action, body }: { action: 'loaded' | 'check' | 'cancel'; body?: object }) =>
      allocation.purchase
        ? api.post(`/billing/purchases/${allocation.purchase.public_id}/${action}`, body ?? {})
        : api.post(`/messaging/provider-accounts/allocations/${allocation.public_id}/${action}`, body ?? {}),
    onSuccess: () => {
      refresh();
      toast.success('Done');
      onClose();
    },
    onError: (e) => toast.error('Not done', refusalText(e)),
  });
  const retry = useMutation({
    mutationFn: () => api.post(`/messaging/provider-accounts/allocations/${allocation.public_id}/retry`, {}),
    onSuccess: () => {
      refresh();
      toast.success('Tried again');
      onClose();
    },
    onError: (e) => toast.error('Not tried', refusalText(e)),
  });
  const panelStep = allocation.method === 'panel' && ['ready', 'waiting_funds', 'failed'].includes(allocation.status);

  return (
    <Drawer open onClose={onClose} title={`${formatNumber(allocation.credits)} SMS credits — ${allocation.client.name}`} width="lg">
      <div className="space-y-4">
        {reconcile ? (
          <Alert tone="info" title="Credits the vendor did not pay for">
            The client already holds these credits in Connect, but its balance at the vendor did not pay for them. This
            load closes the gap. It never changes the client's credits or money.
          </Alert>
        ) : null}
        <Card>
          <CardHeader
            title={allocation.account?.label ?? 'No provider account'}
            actions={<StatusChip value={allocation.status} labels={STATUS_LABELS} tones={STATUS_TONES} />}
          />
          <CardBody className="space-y-1 text-sm">
            <div>
              Load at the client's account: <span className="tabular">{formatMetered(allocation.amount)}</span>
              {allocation.customer_ref ? ` (customer ${allocation.customer_ref})` : ''}
            </div>
            {allocation.account_debit ? (
              <div className="text-muted">
                From the provider account: <span className="tabular">{formatMetered(allocation.account_debit)}</span>
                {allocation.status === 'confirmed' ? ` · ${SYNC_LABELS[allocation.sync_status]}` : ''}
              </div>
            ) : null}
            <div className="text-xs text-faint">
              Reference {allocation.reference}
              {allocation.vendor_reference ? ` · vendor ${allocation.vendor_reference}` : ''}
              {allocation.attempts ? ` · ${allocation.attempts} attempt(s)` : ''}
            </div>
            {allocation.last_error ? <Alert tone="danger">{allocation.last_error}</Alert> : null}
          </CardBody>
        </Card>

        {allocation.status === 'waiting_link' ? (
          <Alert tone="warning" title="Link the client's service first">
            Its SMS service is not linked to a provider account. Link it on the account's Clients section; the purchase
            then goes ahead by itself.
          </Alert>
        ) : null}
        {allocation.status === 'waiting_funds' ? (
          <Alert tone="danger" title="The provider account cannot carry it">
            Buy reseller balance and record it on the account (Buy reseller balance). Waiting purchases resume
            by themselves, oldest first.
          </Alert>
        ) : null}

        {panelStep && canRecord ? (
          <Card>
            <CardHeader
              title="Load it in the vendor's panel"
              actions={
                panelUrl ? (
                  <a
                    href={`${panelUrl}/Customers`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                  >
                    Open the page <ExternalLink className="size-3.5" />
                  </a>
                ) : null
              }
            />
            <CardBody className="space-y-3">
              <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                <li>
                  In Customer Management, find {allocation.client.name}
                  {allocation.customer_ref ? ` (customer ${allocation.customer_ref})` : ''}.
                </li>
                <li>Choose Add SMS Credit. Credit: {formatMetered(allocation.amount)}. Date: today.</li>
                <li>Add SMS Credit, then copy the TXN ID from its Transaction History.</li>
              </ol>
              <div className="flex flex-wrap items-end gap-2">
                <Input label="Vendor's TXN ID" value={reference} onChange={(e) => setReference(e.target.value)} />
                <Button
                  disabled={!reference.trim()}
                  loading={onPurchase.isPending}
                  onClick={() => onPurchase.mutate({ action: 'loaded', body: { reference: reference.trim() } })}
                >
                  Record the load
                </Button>
              </div>
              <p className="text-xs text-muted">
                {reconcile
                  ? 'Recorded as done: the credits are already the client’s.'
                  : 'The credits are added once the client’s balance at the vendor shows the load.'}
              </p>
            </CardBody>
          </Card>
        ) : null}

        {!reconcile && allocation.method === 'panel' && allocation.status === 'submitted' && canApprove ? (
          <Button variant="secondary" loading={onPurchase.isPending} onClick={() => onPurchase.mutate({ action: 'check' })}>
            Check the client's balance now
          </Button>
        ) : null}
        {allocation.method === 'api' && ['ready', 'submitted'].includes(allocation.status) ? (
          <p className="text-sm text-muted">
            Connect is loading this account through the vendor's API and adds the credits when the vendor confirms.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-3">
          {canRetry && ['waiting_link', 'waiting_funds', 'failed'].includes(allocation.status) ? (
            <Button size="sm" variant="secondary" loading={retry.isPending} onClick={() => retry.mutate()}>
              Try again
            </Button>
          ) : null}
          {allocation.purchase &&
          ['failed', 'review', 'waiting_funds', 'waiting_link', 'ready'].includes(allocation.status) ? (
            <Link
              to={`/messaging/credit-purchases?open=${allocation.purchase.public_id}`}
              className="text-sm text-accent hover:underline"
            >
              The purchase — add the credits or return the money
            </Link>
          ) : null}
          {reconcile &&
          canRetry &&
          ['failed', 'review', 'waiting_funds', 'waiting_link', 'ready'].includes(allocation.status) &&
          !(allocation.method === 'api' && allocation.status === 'ready') ? (
            <Button size="sm" variant="ghost" loading={onPurchase.isPending} onClick={() => onPurchase.mutate({ action: 'cancel' })}>
              Don't load it
            </Button>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

export function AlertsList({ alerts, accountId }: { alerts: ProviderAlertRow[]; accountId: string }) {
  const queryClient = useQueryClient();
  const canDismiss = useCan('messaging.provider_account.create');
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/messaging/provider-accounts/alerts/${id}/dismiss`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['provider-accounts', accountId] }),
    onError: (e) => toast.error('Not dismissed', refusalText(e)),
  });
  if (alerts.length === 0) return null;
  return (
    <div className="space-y-2">
      {alerts.map((alert) => (
        <Alert
          key={alert.public_id}
          tone={alert.kind === 'low_balance' || alert.kind === 'sync_review' ? 'warning' : 'danger'}
          title={alert.message}
          onDismiss={canDismiss ? () => dismiss.mutate(alert.public_id) : undefined}
        >
          <span className="text-xs">
            Since {formatDateTime(alert.at)}
            {alert.details.balance ? ` · balance ${formatMetered(String(alert.details.balance))}` : ''}
            {alert.details.balance_at ? ` read ${formatDateTime(String(alert.details.balance_at))}` : ''}
            {alert.details.needed ? ` · needs ${formatMetered(String(alert.details.needed))}` : ''}
            {alert.details.threshold ? ` · threshold ${formatMetered(String(alert.details.threshold))}` : ''}
          </span>
        </Alert>
      ))}
    </div>
  );
}

export function EntriesCard({ entries }: { entries: AccountEntry[] }) {
  if (entries.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No movements recorded"
          description="Reloads, refunds, adjustments and client loads of this account appear here."
        />
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Movements"
        description="The provider account's own money. A refund here is the vendor's credit to the reseller — never a client's purchase, and never shown to a client."
      />
      <ScrollArea className="">
        <table className="w-full text-sm">
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.public_id} className="border-t border-line first:border-0">
                <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{formatDateTime(entry.at)}</td>
                <td className="px-3 py-2">{ENTRY_LABELS[entry.kind]}</td>
                <td className="px-3 py-2 text-xs text-muted">{entry.reference ?? ''}</td>
                <td className="px-3 py-2 text-right tabular">{formatMetered(entry.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </Card>
  );
}
